// Property lifecycle status constants. Mirrors the admin-service
// property_status enum (property-lifecycle-plan.md §1 / §5). FE never
// hardcodes the raw string — always reference PROPERTY_STATUS.
//
// Lifecycle order:
//   draft → submitted → in_verification → revision_requested
//         → in_compliance → in_vasp → approved → archived
//   + terminal: rejected
//
// Deprecated values are kept so legacy rows still resolve to a label/severity:
//   • in_financial_setup — service stopped writing it; old rows may carry it.
//   • sent_to_vasp        — legacy read-only alias of in_vasp.

export enum PROPERTY_STATUS {
  DRAFT = 'draft',
  SUBMITTED = 'submitted',
  IN_VERIFICATION = 'in_verification',
  REVISION_REQUESTED = 'revision_requested',
  IN_COMPLIANCE = 'in_compliance',
  IN_VASP = 'in_vasp',
  APPROVED = 'approved',
  REJECTED = 'rejected',
  ARCHIVED = 'archived',

  // ── Deprecated / legacy (read-only) ──────────────────────────────
  IN_FINANCIAL_SETUP = 'in_financial_setup',
  SENT_TO_VASP = 'sent_to_vasp',
}

/** StatusBadge color per status — drives the canonical status pill. */
export type PropertyStatusSeverity =
  | 'primary'
  | 'success'
  | 'warning'
  | 'danger'
  | 'info'
  | 'neutral'

/** Operator-facing label per status. Match copy across every screen (ux §5). */
export const PROPERTY_STATUS_LABEL: Record<PROPERTY_STATUS, string> = {
  [PROPERTY_STATUS.DRAFT]: 'Draft',
  [PROPERTY_STATUS.SUBMITTED]: 'Submitted',
  [PROPERTY_STATUS.IN_VERIFICATION]: 'In Verification',
  [PROPERTY_STATUS.REVISION_REQUESTED]: 'Revision Requested',
  [PROPERTY_STATUS.IN_COMPLIANCE]: 'In Compliance',
  [PROPERTY_STATUS.IN_VASP]: 'In VASP Onboarding',
  [PROPERTY_STATUS.APPROVED]: 'Approved',
  [PROPERTY_STATUS.REJECTED]: 'Rejected',
  [PROPERTY_STATUS.ARCHIVED]: 'Archived',
  [PROPERTY_STATUS.IN_FINANCIAL_SETUP]: 'In Financial Setup',
  [PROPERTY_STATUS.SENT_TO_VASP]: 'Sent to VASP',
}

export const PROPERTY_STATUS_SEVERITY: Record<PROPERTY_STATUS, PropertyStatusSeverity> = {
  [PROPERTY_STATUS.DRAFT]: 'neutral',
  [PROPERTY_STATUS.SUBMITTED]: 'info',
  [PROPERTY_STATUS.IN_VERIFICATION]: 'info',
  [PROPERTY_STATUS.REVISION_REQUESTED]: 'warning',
  [PROPERTY_STATUS.IN_COMPLIANCE]: 'info',
  [PROPERTY_STATUS.IN_VASP]: 'info',
  [PROPERTY_STATUS.APPROVED]: 'success',
  [PROPERTY_STATUS.REJECTED]: 'danger',
  [PROPERTY_STATUS.ARCHIVED]: 'neutral',
  [PROPERTY_STATUS.IN_FINANCIAL_SETUP]: 'warning',
  [PROPERTY_STATUS.SENT_TO_VASP]: 'info',
}

/** Resolve a raw status string (any case) to its label, falling back to the raw value. */
export function propertyStatusLabel(status: string | null | undefined): string {
  if (!status) return '—'
  const key = status.toLowerCase() as PROPERTY_STATUS
  return PROPERTY_STATUS_LABEL[key] ?? status
}

/** Resolve a raw status string (any case) to its StatusBadge severity. */
export function propertyStatusSeverity(status: string | null | undefined): PropertyStatusSeverity {
  if (!status) return 'neutral'
  const key = status.toLowerCase() as PROPERTY_STATUS
  return PROPERTY_STATUS_SEVERITY[key] ?? 'neutral'
}
